import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { t } from '../../i18n'
import { Button } from '../Card'
import { ArrowUpIcon } from './CheckMark'

const ROW = 40

export type TimePreset = { label: string; value: string }

export function TimeField({
  value,
  onChange,
  presets,
  hint,
  hourLabel,
  minuteLabel,
}: {
  value: string
  onChange: (value: string) => void
  presets?: TimePreset[]
  hint?: string
  hourLabel: string
  minuteLabel: string
}) {
  const coarse = usePointerCoarse()
  const [typing, setTyping] = useState(false)

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-hint text-text-muted">{hint}</span>
        {coarse ? (
          <SmallButton onClick={() => setTyping((current) => !current)}>
            {typing ? t.time.wheels : t.time.digits}
          </SmallButton>
        ) : null}
      </div>

      <div className="mt-[6px]">
        {coarse && !typing ? (
          <Wheels
            value={value}
            onChange={onChange}
            hourLabel={hourLabel}
            minuteLabel={minuteLabel}
          />
        ) : (
          <Typed
            value={value}
            onChange={onChange}
            stepper={!coarse}
            onDone={() => setTyping(false)}
            done={coarse}
          />
        )}
      </div>

      {presets && presets.length > 0 ? (
        <div className="mt-3 flex gap-[6px]">
          {presets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onChange(preset.value)}
              className={`pressable tap-area min-h-8 flex-1 rounded-chip border px-[6px] text-note font-medium transition-colors duration-row ${
                preset.value === value
                  ? 'border-accent bg-tint text-accent-deep'
                  : 'border-hairline bg-field text-text-strong'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function Wheels({
  value,
  onChange,
  hourLabel,
  minuteLabel,
}: {
  value: string
  onChange: (value: string) => void
  hourLabel: string
  minuteLabel: string
}) {
  const [hours, minutes] = split(value)

  return (
    <div className="relative h-[196px] overflow-hidden rounded-nested bg-glass-2">
      <span
        aria-hidden
        className="pointer-events-none absolute left-6 right-6 top-[78px] h-10 rounded-chip bg-field-focus shadow-card"
      />

      <div className="absolute inset-0 flex justify-center">
        <Wheel
          label={hourLabel}
          count={24}
          value={hours}
          onChange={(next) => onChange(`${pad(next)}:${pad(minutes)}`)}
        />
        <span className="flex w-[14px] items-center justify-center text-wheel text-text-secondary">
          :
        </span>
        <Wheel
          label={minuteLabel}
          count={60}
          value={minutes}
          onChange={(next) => onChange(`${pad(hours)}:${pad(next)}`)}
        />
      </div>

      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-linear-to-b from-bg to-transparent"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-bg to-transparent"
      />
    </div>
  )
}

function Wheel({
  label,
  count,
  value,
  onChange,
}: {
  label: string
  count: number
  value: number
  onChange: (value: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const scrolling = useRef<number | undefined>(undefined)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node || scrolling.current !== undefined) return
    const target = value * ROW
    if (Math.abs(node.scrollTop - target) > 2) node.scrollTop = target
  }, [value])

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label={label}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          onChange((value - 1 + count) % count)
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          onChange((value + 1) % count)
        }
      }}
      onScroll={(event) => {
        const node = event.currentTarget
        window.clearTimeout(scrolling.current)
        scrolling.current = window.setTimeout(() => {
          scrolling.current = undefined
        }, 180)
        const next = Math.max(0, Math.min(count - 1, Math.round(node.scrollTop / ROW)))
        if (next !== value) onChange(next)
      }}
      className="no-scrollbar h-[196px] w-[78px] snap-y snap-mandatory overflow-y-auto py-[78px] text-center outline-hidden overscroll-contain [touch-action:pan-y]"
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          role="option"
          aria-selected={index === value}
          className={`tabular flex h-10 snap-center items-center justify-center text-wheel ${
            index === value ? 'text-text' : 'text-text-muted'
          }`}
        >
          {pad(index)}
        </div>
      ))}
    </div>
  )
}

function Typed({
  value,
  onChange,
  stepper,
  done,
  onDone,
}: {
  value: string
  onChange: (value: string) => void
  stepper: boolean
  done: boolean
  onDone: () => void
}) {
  const [text, setText] = useState(value)
  const [bad, setBad] = useState(false)
  const [shown, setShown] = useState(value)

  if (shown !== value) {
    setShown(value)
    setText(value)
  }

  function commit(raw: string): boolean {
    if (raw.trim() === '') {
      setBad(false)
      setText(value)
      return true
    }

    const parsed = parseTime(raw)
    if (!parsed) {
      setBad(true)
      setText(value)
      return false
    }
    setBad(false)
    setText(parsed)
    onChange(parsed)
    return true
  }

  function step(minutes: number) {
    const [hours, mins] = split(parseTime(text) ?? value)
    const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440
    onChange(`${pad(Math.floor(total / 60))}:${pad(total % 60)}`)
  }

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex gap-[7px]">
        <div
          className={`flex h-field min-w-0 flex-1 items-center gap-[6px] overflow-hidden rounded-nested border bg-field pl-4 pr-2 ${
            bad ? 'border-danger' : 'border-hairline'
          } focus-within:border-accent focus-within:bg-field-focus focus-within:shadow-focus`}
        >
          <input
            value={text}
            inputMode="numeric"
            maxLength={5}
            placeholder="07:50"
            onChange={(event) => {
              setBad(false)
              setText(mask(event.target.value))
            }}
            onBlur={(event) => commit(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur()
                if (done) onDone()
                return
              }
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault()
                const amount = event.shiftKey ? 60 : 1
                step(event.key === 'ArrowUp' ? amount : -amount)
              }
            }}
            className="tabular min-w-0 flex-1 border-0 bg-transparent text-wheel text-text outline-hidden placeholder:text-text-muted"
          />
          {stepper ? (
            <span className="flex flex-none flex-col gap-[3px]">
              <StepButton label={t.time.later} onClick={() => step(5)} />
              <StepButton label={t.time.earlier} onClick={() => step(-5)} down />
            </span>
          ) : null}
        </div>
      </div>

      {bad ? <div className="text-hint text-danger">{t.time.badFormat}</div> : null}

      {done ? (
        <Button
          onClick={() => {
            if (commit(text)) onDone()
          }}
        >
          {t.common.done}
        </Button>
      ) : null}
    </div>
  )
}

function StepButton({
  label,
  onClick,
  down,
}: {
  label: string
  onClick: () => void
  down?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`flex h-[21px] w-[30px] items-center justify-center rounded-stepper text-text-secondary transition-colors duration-press hover:bg-fill active:bg-fill ${
        down ? 'rotate-180' : ''
      }`}
    >
      <ArrowUpIcon size={12} />
    </button>
  )
}

function SmallButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="pressable tap-area min-h-7 rounded-chip border border-hairline bg-field px-[9px] text-note font-medium text-text-strong"
    >
      {children}
    </button>
  )
}

function usePointerCoarse(): boolean {
  const [coarse, setCoarse] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(pointer: coarse)').matches,
  )

  useEffect(() => {
    const query = window.matchMedia('(pointer: coarse)')
    const update = () => setCoarse(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return coarse
}

function mask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 1) return digits
  if (digits.length === 2) {
    return Number(digits) > 23 ? `0${digits[0]}:${digits[1]}` : digits
  }
  if (digits.length === 3) {
    return Number(digits.slice(0, 2)) <= 23
      ? `${digits.slice(0, 2)}:${digits[2]}`
      : `0${digits[0]}:${digits.slice(1, 3)}`
  }
  const hours = Math.min(23, Number(digits.slice(0, 2)))
  const minutes = Math.min(59, Number(digits.slice(2)))
  return `${pad(hours)}:${pad(minutes)}`
}

function parseTime(raw: string): string | undefined {
  const spaced = /^(\d{1,2})(?:\D+(\d{1,2}))?$/.exec(raw.trim())
  let hours: number
  let minutes: number

  if (spaced) {
    hours = Number(spaced[1])
    minutes = spaced[2] === undefined ? 0 : Number(spaced[2])
  } else {
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 3) {
      hours = Number(digits[0])
      minutes = Number(digits.slice(1))
    } else if (digits.length === 4) {
      hours = Number(digits.slice(0, 2))
      minutes = Number(digits.slice(2))
    } else {
      return undefined
    }
  }

  if (hours > 23 || minutes > 59) return undefined
  return `${pad(hours)}:${pad(minutes)}`
}

function split(value: string): [number, number] {
  const [hours, minutes] = value.split(':')
  return [clamp(Number(hours), 23), clamp(Number(minutes), 59)]
}

function clamp(value: number, max: number): number {
  if (!Number.isFinite(value) || value < 0) return 0
  return Math.min(Math.round(value), max)
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}
